import { overtimeConstants } from './OvertimeConstants';
import { saveNoContactHolds, loadNoContactHolds, removeNoContactHolds } from './draftPersistence';
import {
    isNoContactAction,
    normalizeActionToken,
    parseCallLogTimestampMs,
    normalizeShiftToken,
    normalizeShiftType,
    normalizeSlotTimeToken,
    hasSlotDetails,
} from './callLogUtils';

// ============================================
// Utility Functions for Normalization
// ============================================

const normalizeNoContactTimerShiftToken = (value = '') => normalizeShiftToken(value);

/**
 * Pure merge function — applies persisted decisions (passed in via action payload)
 * onto freshly normalized signups. No side effects (no sessionStorage access).
 */
const mergeWithPersistedDecisions = (shiftSignups, persistedDecisions) => {
  if (!persistedDecisions || Object.keys(persistedDecisions).length === 0) return shiftSignups;

  const updatedById = { ...shiftSignups.byId };
  let changed = false;

  Object.entries(persistedDecisions).forEach(([signupId, decision]) => {
    const signup = updatedById[signupId];
    if (!signup) return;

    const apiStatus = (signup.status || '').toUpperCase();
    const confirmedStatus = (decision.status || '').toUpperCase();

    // Don't override if API already shows a terminal state
    const terminalStatuses = ['ASSIGNED', 'WBSUCCESS', 'WB_SUCCESS', 'REFUSED', 'WB_FAILED', 'WBFAILED'];
    if (terminalStatuses.includes(apiStatus.replace(/[\s_]/g, ''))) return;

    if (confirmedStatus === 'ACCEPT' || confirmedStatus === 'REFUSED') {
      updatedById[signupId] = {
        ...signup,
        status: decision.status,
        reqId: decision.reqId || signup.reqId || '',
        transactionId: decision.transactionId || signup.transactionId || '',
        lastTimeStamp: decision.lastTimeStamp || signup.lastTimeStamp || '',
        savedAt: decision.savedAt,
      };
      changed = true;
    }
  });

  if (!changed) return shiftSignups;
  return { ...shiftSignups, byId: updatedById };
};

/**
 * Creates a composite ID for shift signups
 */
const createSignupId = (employeeId, signupOrShiftName) => {
    if (typeof signupOrShiftName === 'string') {
        return `${employeeId}-${signupOrShiftName}`;
    }

    const signup = signupOrShiftName || {};
    const shiftName = signup.shiftName || '';
    const startTime = signup.startTime || '';
    const endTime = signup.endTime || '';
    const workLocation = signup.workLocation || '';
    // Use votRequestId for identification (stable across decision changes)
    // Do NOT include transactionId, as it changes when decision is modified and creates duplicate cards
    const requestId = signup.votRequestId || '';
    const uniqueParts = [startTime, endTime, workLocation, requestId].filter(Boolean);

    if (uniqueParts.length === 0) {
        return `${employeeId}-${shiftName}`;
    }

    return `${employeeId}-${shiftName}-${uniqueParts.join('-')}`;
};

/**
 * Deduplicates signups without transactionId against existing signups.
 * Signups without transactionId represent saved decisions (SAVE-ACCEPTED/SAVE-REFUSED).
 * If a saved decision matches an existing signup by shiftName, shiftDate, workLocation, startTime, endTime,
 * drop the incoming saved-decision signup (the existing signup already represents that shift).
 * Otherwise, include the incoming saved-decision signup.
 */
const deduplicateSavedDecisions = (incomingSignups, existingSignupsByEmployeeId) => {
    const resultSignups = [...incomingSignups];
    const indexesToRemove = new Set();

    resultSignups.forEach((incoming, incomingIndex) => {
        // Only process signups that are actual saved decisions (no transactionId + SAVE status).
        // Fresh callsheet signups also lack transactionId, so we must additionally check the status
        // to avoid incorrectly removing legitimate new signups during shift transitions.
        if (incoming.transactionId) return;
        const incomingStatus = (incoming.selectedDecision || incoming.status || '').toUpperCase();
        if (!['SAVE-ACCEPTED', 'SAVE-REFUSED', 'SAVE-APPLY'].includes(incomingStatus)) return;

        const employeeId = incoming.employeeId || '';
        const existingForEmployee = existingSignupsByEmployeeId[employeeId] || [];

        // Try to find a matching existing signup
        const matchFound = existingForEmployee.some((existingSignup) => {
            const incomingShiftName = (incoming.shiftName || '').toUpperCase().trim();
            const existingShiftName = (existingSignup.shiftName || '').toUpperCase().trim();
            const incomingShiftDate = (incoming.shiftDate || '').trim();
            const existingShiftDate = (existingSignup.shiftDate || '').trim();
            const incomingWorkLocation = (incoming.workLocation || '').toUpperCase().trim();
            const existingWorkLocation = (existingSignup.workLocation || '').toUpperCase().trim();
            const incomingStartTime = (incoming.startTime || '').trim();
            const existingStartTime = (existingSignup.startTime || '').trim();
            const incomingEndTime = (incoming.endTime || '').trim();
            const existingEndTime = (existingSignup.endTime || '').trim();

            // Match on all fields
            const matches = incomingShiftName === existingShiftName &&
                incomingShiftDate === existingShiftDate &&
                incomingWorkLocation === existingWorkLocation &&
                incomingStartTime === existingStartTime &&
                incomingEndTime === existingEndTime;

            return matches;
        });

        // If match found, mark this incoming signup for removal (keep the existing one)
        if (matchFound) {
            indexesToRemove.add(incomingIndex);
        }
    });

    // Remove matched signups (keep existing ones)
    return resultSignups.filter((_, index) => !indexesToRemove.has(index));
};

/**
 * Sorts signup IDs within byEmployeeId arrays by their startTime in ascending order.
 * Signups without startTime are placed at the end, maintaining stability for equal values.
 */
const sortSignupsByStartTime = (shiftSignups) => {
    const sortedByEmployeeId = { ...shiftSignups.byEmployeeId };
    
    Object.keys(sortedByEmployeeId).forEach((employeeId) => {
        const signupIds = sortedByEmployeeId[employeeId] || [];
        sortedByEmployeeId[employeeId] = [...signupIds].sort((idA, idB) => {
            const signupA = shiftSignups.byId[idA];
            const signupB = shiftSignups.byId[idB];
            
            const startTimeA = String(signupA?.startTime || '');
            const startTimeB = String(signupB?.startTime || '');
            
            // If both have startTime, compare them as HH:MM (lexicographic works for 24-hour format)
            if (startTimeA && startTimeB) {
                return startTimeA.localeCompare(startTimeB);
            }
            
            // Signups without startTime go to the end
            if (!startTimeA && startTimeB) return 1;
            if (startTimeA && !startTimeB) return -1;
            
            // Both missing startTime: maintain original order
            return 0;
        });
    });
    
    return { ...shiftSignups, byEmployeeId: sortedByEmployeeId };
};

const resolveSignupId = (state, action) => {
    if (action.signupId && state.shiftSignups.byId[action.signupId]) {
        return action.signupId;
    }

    const legacyId = createSignupId(action.employeeId, action.shiftName);
    if (state.shiftSignups.byId[legacyId]) {
        return legacyId;
    }

    const employeeSignupIds = state.shiftSignups.byEmployeeId[action.employeeId] || [];
    if (employeeSignupIds.length === 0) {
        const normalizeToken = (value) => String(value || '').toUpperCase().trim();
        const actionShiftToken = normalizeToken(action.shiftName || action.shiftData?.shiftType);
        if (!actionShiftToken) return undefined;
        const fallbackById = Object.keys(state.shiftSignups.byId).find((id) => {
            const signup = state.shiftSignups.byId[id];
            if (!signup) return false;
            return (
                String(signup.employeeId || '') === String(action.employeeId || '') &&
                normalizeToken(signup.shiftName || signup.shiftType) === actionShiftToken
            );
        });
        return fallbackById;
    }

    const actionTransactionId = String(action.transactionId || action.shiftData?.transactionId || '').trim();
    if (actionTransactionId) {
        const byTransactionId = employeeSignupIds.find((id) => {
            const signup = state.shiftSignups.byId[id];
            return String(signup?.transactionId || '').trim() === actionTransactionId;
        });
        if (byTransactionId) return byTransactionId;
    }

    const actionReqId = String(action.reqId || '').trim();
    if (actionReqId) {
        const byReqId = employeeSignupIds.find((id) => {
            const signup = state.shiftSignups.byId[id];
            return String(signup?.reqId || '').trim() === actionReqId;
        });
        if (byReqId) return byReqId;
    }

    const normalizeToken = (value) => String(value || '').toUpperCase().trim();
    const actionShiftToken = normalizeToken(action.shiftName || action.shiftData?.shiftType);
    const actionStartToken = normalizeSlotTimeToken(action.startTime || action.shiftData?.startTime);
    const actionEndToken = normalizeSlotTimeToken(action.endTime || action.shiftData?.endTime);
    const actionLocationToken = normalizeToken(action.workLocation || action.shiftData?.workLocation);

    const hasDetailTokens = Boolean(actionStartToken || actionEndToken || actionLocationToken);
    if (actionShiftToken && hasDetailTokens) {
        const byDetails = employeeSignupIds.find((id) => {
            const signup = state.shiftSignups.byId[id];
            if (!signup) return false;
            const signupShiftToken = normalizeToken(signup.shiftName || signup.shiftType);
            if (signupShiftToken !== actionShiftToken) return false;

            return (
                normalizeSlotTimeToken(signup.startTime) === actionStartToken &&
                normalizeSlotTimeToken(signup.endTime) === actionEndToken &&
                normalizeToken(signup.workLocation) === actionLocationToken
            );
        });
        if (byDetails) return byDetails;
    }

    if (actionShiftToken) {
        const byShiftName = employeeSignupIds.find((id) => {
            const signup = state.shiftSignups.byId[id];
            return normalizeToken(signup?.shiftName || signup?.shiftType) === actionShiftToken;
        });
        if (byShiftName) return byShiftName;
    }

    return employeeSignupIds[0];
};

/**
 * Update a field on an OT signup entry when it doesn't exist in shiftSignups.byId.
 * Returns the new state if a match was found, or null if no match.
 */
const updateOtSignupField = (state, action, fields) => {
    const empId = action.employeeId;
    const groups = state.otSignups.byEmployeeId[empId];
    if (!groups) return null;

    let found = false;
    const updateArr = (arr) =>
        (arr || []).map((shift) => {
            const isMatch = shift.id === action.signupId ||
                (action.shiftName && (shift.shiftName || shift.shiftType) === action.shiftName);
            if (isMatch) {
                found = true;
                return { ...shift, ...fields };
            }
            return shift;
        });

    const updatedGroups = {
        otDayShifts: updateArr(groups.otDayShifts),
        previousDayShifts: updateArr(groups.previousDayShifts),
        nextDayShifts: updateArr(groups.nextDayShifts),
    };

    if (!found) return null;

    return {
        ...state,
        otSignups: {
            byEmployeeId: {
                ...state.otSignups.byEmployeeId,
                [empId]: updatedGroups,
            },
        },
    };
};

/**
 * Normalizes an array of employees from API response
 * into normalized state structure
 */
export const normalizeEmployees = (employeesArray) => {
    const employees = {
        byId: {},
        allIds: []
    };
    
    const shiftSignups = {
        byId: {},
        byEmployeeId: {}
    };

    employeesArray.forEach((employee, index) => {
        const { otShiftSignUps, ...employeeData } = employee;
        const effectiveDepartment = employee.department || '';
        const normalizedOtGroup = effectiveDepartment === 'Customer Care' ? (employee.otGroup || '') : '';
        const normalizedSchedBidGroup = effectiveDepartment === 'Customer Operations' ? (employee.schedBidGroup || '') : '';
        
        // Store employee without nested signups
        // First employee expanded, rest collapsed
        employees.byId[employee.aaId] = {
            ...employeeData,
            collapsed: index !== 0
        };
        employees.allIds.push(employee.aaId);
        
        // Normalize shift signups
        shiftSignups.byEmployeeId[employee.aaId] = [];
        
        if (otShiftSignUps) {
            const shiftDate = employeeData.otDayShifts?.[0]?.shiftDate || '';
            otShiftSignUps.forEach((signup) => {
                const signupId = createSignupId(employee.aaId, signup);
                shiftSignups.byId[signupId] = {
                    ...signup,
                    id: signupId,
                    employeeId: employee.aaId,
                    schedBidGroup: normalizedSchedBidGroup,
                    otGroup: normalizedOtGroup,
                    shiftDate,
                };
                shiftSignups.byEmployeeId[employee.aaId].push(signupId);
            });
        }
    });

    // Sort signups by startTime for each employee
    return { employees, shiftSignups: sortSignupsByStartTime(shiftSignups) };
};

// ============================================
// Initial State (Normalized)
// ============================================
const initialState = {
    employees: { byId: {}, allIds: [] },
    shiftSignups: { byId: {}, byEmployeeId: {} },
    overtimeSlots: [],
    showLoader: false,
    fetchSuccess: null,
    fetchError: null,
    otSkillsSuccess: null,
    otSkillsData: null,
    sessionStatusData: null,
    sessionStatusSuccess: null,
    sessionStatusError: null,
    callSheetData: null,
    requirementFulfilled: false,
    votReportData: [],
    votReportLoading: false,
    votReportError: null,
    votReportDetailData: {},
    votReportDetailLoading: false,
    votReportDetailError: null,
    votCallsheetData: [],
    votCallsheetLoading: false,
    votCallsheetError: null,
    pendingCount: null,
    wbPolling: false,
    callLogEntries: {},
    noContactTimersByEmployee: {},
    timerNowMs: 0,
    continueInProgress: false,
    otSignups: { byEmployeeId: {} },
    noContactHolds: loadNoContactHolds(),
    duplicateModal: {
        status: null,
        show: false,
        message: '',
        error: false,
        existingShift: null
    }
};

/**
 * Derives pendingCount from normalized signups when the API does not provide it.
 * Counts signups that are awaiting Workbrain confirmation for both accept and
 * refuse flows. Refuse is treated the same as accept for dynamic UI updates.
 * TODO: Remove mock once API provides pendingCount
 */
const derivePendingCount = (shiftSignups) => {
    let count = 0;
    Object.values(shiftSignups.byId).forEach((signup) => {
        const status = String(signup.status || '').replace(/[\s_-]/g, '').toUpperCase();
        const wbStatus = String(signup.wbStatus || '').replace(/[\s_]/g, '').toUpperCase();
        const isWbSuccess = signup.wbStatus === true || wbStatus === 'WBSUCCESS';
        const isWbFailed = wbStatus === 'WBFAILED';
        const isAccept = status === 'ACCEPT';
        const isRefuse = status === 'REFUSE';
        const isWaitingForWb = !isWbSuccess && !isWbFailed;

        // Accept-path: ACCEPT status waiting for WB confirmation
        if (isAccept && isWaitingForWb) {
            count++;
        // Refuse-path: REFUSE (intermediate) waiting for WB confirmation
        // REFUSED is terminal — WB already confirmed the refusal
        } else if (isRefuse && isWaitingForWb) {
            count++;
        }
    });
    return count;
};

const isSlotFilledStatus = (status) => {
    const normalized = String(status || '').replace(/[\s_-]/g, '').toUpperCase();
    return normalized === 'ACCEPT'
        || normalized === 'ASSIGNED'
        || normalized === 'WBSUCCESS';
};

const NO_CONTACT_HOLD_MS = 10 * 60 * 1000;

const isNoContactDecision = (value) =>
    String(value || '').replace(/[\s_-]/g, '').toUpperCase() === 'NOCONTACT';

const isCustomerCareDepartment = (department = '') =>
    String(department || '').trim().toUpperCase() === 'CUSTOMER CARE';

const buildNoContactGroupKey = (shiftType, startTime, endTime, workLocation) =>
    [
        shiftType,
        normalizeSlotTimeToken(startTime),
        normalizeSlotTimeToken(endTime),
        workLocation,
    ]
        .map((v) => normalizeShiftType(v || ''))
        .join('|');

const parseSignupTimestampMs = (value) => {
    const parsed = parseCallLogTimestampMs(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const deriveNoContactTimersByEmployee = (shiftSignups, currentShiftName = '') => {
    const currentShiftToken = normalizeShiftType(currentShiftName);
    const timersByEmployee = {};

    Object.values(shiftSignups?.byId || {}).forEach((signup) => {
        if (!signup || signup.noContactHoldApplied !== true) return;

        const signupShiftToken = normalizeShiftType(signup.shiftName || signup.shiftType);
        if (currentShiftToken && signupShiftToken !== currentShiftToken) return;

        const employeeId = String(signup.employeeId || '').trim();
        const holdUntilMs = Number(signup.noContactHoldUntilMs) || 0;
        if (!employeeId || holdUntilMs <= 0) return;

        const existing = timersByEmployee[employeeId];
        if (!existing || holdUntilMs > Number(existing.holdUntilMs || 0)) {
            timersByEmployee[employeeId] = {
                holdUntilMs,
                ownerEmployeeId: String(signup.noContactHoldSourceEmployeeId || signup.employeeId || '').trim(),
                shiftType: signup.shiftName || signup.shiftType || '',
            };
        }
    });

    return timersByEmployee;
};

const applyNoContactHoldsFromSignups = (shiftSignups, currentShiftName, nowMs = Date.now()) => {
    const currentShift = normalizeShiftType(currentShiftName);
    if (!currentShift) return shiftSignups;

    const byId = shiftSignups?.byId || {};
    const activeHoldByMatchKey = new Map();

    Object.values(byId).forEach((signup) => {
        const signupShift = normalizeShiftType(signup?.shiftName || signup?.shiftType || '');
        if (!signup || signup.isEligible === false || signupShift !== currentShift) return;

        const noContact = isNoContactDecision(signup.selectedDecision) || isNoContactDecision(signup.status);
        if (!noContact) return;
        // Skip holds that were intentionally released this session — don't re-apply on data refetch.
        if (signup.noContactResolved === true) return;

        const sourceTsMs = parseSignupTimestampMs(signup.lastTimeStamp || signup.transactionTime);
        if (!sourceTsMs) return;

        const holdUntilMs = sourceTsMs + NO_CONTACT_HOLD_MS;
        if (holdUntilMs <= nowMs) return;

        const matchKey = buildNoContactGroupKey(
            signup.shiftName || signup.shiftType,
            signup.startTime,
            signup.endTime,
            signup.workLocation
        );
        const ownerEmployeeId = String(signup.employeeId || '').trim();
        const existing = activeHoldByMatchKey.get(matchKey);
        if (!existing || holdUntilMs > existing.holdUntilMs) {
            activeHoldByMatchKey.set(matchKey, { holdUntilMs, ownerEmployeeId });
        }
    });

    if (activeHoldByMatchKey.size === 0) return shiftSignups;

    const updatedById = { ...byId };
    activeHoldByMatchKey.forEach((hold, matchKey) => {
        Object.keys(updatedById).forEach((id) => {
            const signup = updatedById[id];
            const signupKey = buildNoContactGroupKey(
                signup.shiftName || signup.shiftType,
                signup.startTime,
                signup.endTime,
                signup.workLocation
            );
            if (signupKey !== matchKey) return;

            const currentSlotsTaken = Number(signup.slotsTaken) || 0;
            const alreadyApplied = signup.noContactHoldApplied === true;
            updatedById[id] = {
                ...signup,
                slotsTaken: String(alreadyApplied ? currentSlotsTaken : (currentSlotsTaken + 1)),
                noContactHoldApplied: true,
                noContactHoldUntilMs: hold.holdUntilMs,
                noContactHoldSourceEmployeeId: hold.ownerEmployeeId,
            };
        });
    });

    return {
        ...shiftSignups,
        byId: updatedById,
    };
};

const updateSlotsFilledCount = (callSheetData, previousStatus, nextStatus) => {
    if (!callSheetData?.callSheetWorkingShift) {
        return callSheetData;
    }

    const wasFilled = isSlotFilledStatus(previousStatus);
    const isFilled = isSlotFilledStatus(nextStatus);

    if (wasFilled === isFilled) {
        return callSheetData;
    }

    const currentFilled = Number(callSheetData.callSheetWorkingShift.slotsFilled) || 0;
    const totalSlots = Number(callSheetData.callSheetWorkingShift.slots) || 0;
    let nextFilled = currentFilled + (isFilled ? 1 : -1);

    nextFilled = Math.max(0, nextFilled);
    if (totalSlots > 0) {
        nextFilled = Math.min(nextFilled, totalSlots);
    }

    return {
        ...callSheetData,
        callSheetWorkingShift: {
            ...callSheetData.callSheetWorkingShift,
            slotsFilled: nextFilled,
        },
    };
};

// ============================================
// Reducer
// ============================================
export const OvertimeReducer = (state = initialState, action) => {
    switch (action.type) {
        case overtimeConstants.SHOW_OVERTIME_LOADER: {
            return {
                ...state,
                showLoader: action.data
            };
        }

        case overtimeConstants.GET_OVERTIME_DATA_SUCCESS: {
            // Normalize incoming data from API
            const callSheetList = Array.isArray(action.data?.callSheetList) 
                ? action.data.callSheetList 
                : [];
            const selectedDepartment = action.requestParams?.department || '';
            const callSheetListWithDepartment = selectedDepartment
                ? callSheetList.map((employee) => ({ ...employee, department: selectedDepartment }))
                : callSheetList;
            const { employees, shiftSignups: incomingShiftSignups } = normalizeEmployees(callSheetListWithDepartment);
            
            // Deduplicate saved decisions (SAVE-ACCEPTED/SAVE-REFUSED) against existing signups
            const incomingSignupsArray = Object.values(incomingShiftSignups.byId);
            const existingSignupsByEmployeeId = {};
            Object.entries(state.shiftSignups.byEmployeeId || {}).forEach(([empId, signupIds]) => {
                existingSignupsByEmployeeId[empId] = signupIds
                    .map((id) => state.shiftSignups.byId[id])
                    .filter(Boolean);
            });
            const dedupedSignupsArray = deduplicateSavedDecisions(incomingSignupsArray, existingSignupsByEmployeeId);
            
            // Merge deduplicated incoming signups with existing state signups
            // Preserve local/UI-only fields (callLogGateOpen, isConfirming, selectedDecision, wbStatus, error, etc.)
            const mergedShiftSignupsById = { ...state.shiftSignups.byId };
            // Deep-copy inner arrays to prevent mutating the previous Redux state
            const mergedByEmployeeId = {};
            Object.entries(state.shiftSignups.byEmployeeId).forEach(([empId, ids]) => {
                mergedByEmployeeId[empId] = [...ids];
            });
            
            // Track which signups are in the incoming data (to avoid duplicates)
            const incomingIds = new Set(dedupedSignupsArray.map(s => s.id));
            
            // Helper: find an existing signup that matches by field comparison
            const findExistingByFields = (signup) => {
                const empId = signup.employeeId;
                const existingIds = mergedByEmployeeId[empId] || [];
                const sShiftName = (signup.shiftName || signup.shiftType || '').toUpperCase().trim();
                const sStart = (signup.startTime || '').trim();
                const sEnd = (signup.endTime || '').trim();
                const sWorkLoc = (signup.workLocation || '').toUpperCase().trim();
                const sDate = (signup.shiftDate || '').trim();
                return existingIds
                    .map((id) => mergedShiftSignupsById[id])
                    .filter(Boolean)
                    .find((existing) => {
                        const eShiftName = (existing.shiftName || existing.shiftType || '').toUpperCase().trim();
                        const eStart = (existing.startTime || '').trim();
                        const eEnd = (existing.endTime || '').trim();
                        const eWorkLoc = (existing.workLocation || '').toUpperCase().trim();
                        const eDate = (existing.shiftDate || '').trim();
                        return sShiftName === eShiftName &&
                            sStart === eStart &&
                            sEnd === eEnd &&
                            sWorkLoc === eWorkLoc &&
                            sDate === eDate;
                    });
            };
            
            // UI-only fields that should not be overwritten by API data
            const UI_ONLY_FIELDS = ['callLogGateOpen', 'isConfirming', 'pendingDecision', 'error'];
            
            const preserveUiFields = (existing) => {
                const preserved = {};
                UI_ONLY_FIELDS.forEach((field) => {
                    if (existing[field] !== undefined) {
                        preserved[field] = existing[field];
                    }
                });
                // Preserve selectedDecision/wbStatus only if they represent in-progress local state
                // (i.e., don't overwrite a local REFUSE/WB_PENDING with stale API data)
                if (existing.selectedDecision !== undefined) {
                    preserved.selectedDecision = existing.selectedDecision;
                }
                if (existing.wbStatus !== undefined) {
                    preserved.wbStatus = existing.wbStatus;
                }
                return preserved;
            };
            
            // Update or add incoming signups with smart merge
            dedupedSignupsArray.forEach((signup) => {
                const incomingTxnId = signup.transactionId ? String(signup.transactionId).trim() : null;
                let existingSignup = null;
                
                // Strategy 1: Match by transactionId
                if (incomingTxnId) {
                    existingSignup = mergedShiftSignupsById[signup.id];
                    // Also check if an existing signup shares the same transactionId but different id
                    if (!existingSignup || String(existingSignup.transactionId || '').trim() !== incomingTxnId) {
                        const empIds = mergedByEmployeeId[signup.employeeId] || [];
                        const txnMatch = empIds
                            .map((id) => mergedShiftSignupsById[id])
                            .filter(Boolean)
                            .find((s) => String(s.transactionId || '').trim() === incomingTxnId);
                        if (txnMatch) existingSignup = txnMatch;
                    }
                }
                
                // Strategy 2: No transactionId + saved decision → match by fields
                if (!existingSignup && !incomingTxnId) {
                    const savedStatus = (signup.selectedDecision || signup.status || '').toUpperCase();
                    const isSavedDecision = ['SAVE-ACCEPTED', 'SAVE-REFUSED', 'SAVE-APPLY'].includes(savedStatus);
                    if (isSavedDecision) {
                        existingSignup = findExistingByFields(signup);
                    }
                }
                
                // Fallback: direct id match (existing object)
                if (!existingSignup) {
                    existingSignup = mergedShiftSignupsById[signup.id] || null;
                }
                
                // Merge: preserve UI fields from existing, overwrite API fields from incoming
                if (existingSignup) {
                    const uiFields = preserveUiFields(existingSignup);
                    // If the existing signup had a different id (matched by txnId or fields),
                    // remove the old entry and use the incoming id
                    if (existingSignup.id && existingSignup.id !== signup.id) {
                        delete mergedShiftSignupsById[existingSignup.id];
                        const empId = existingSignup.employeeId;
                        if (empId && mergedByEmployeeId[empId]) {
                            mergedByEmployeeId[empId] = mergedByEmployeeId[empId].filter(sid => sid !== existingSignup.id);
                        }
                    }
                    mergedShiftSignupsById[signup.id] = { ...existingSignup, ...signup, ...uiFields };
                } else {
                    mergedShiftSignupsById[signup.id] = signup;
                }
                
                const empId = signup.employeeId;
                if (!mergedByEmployeeId[empId]) {
                    mergedByEmployeeId[empId] = [];
                }
                if (!mergedByEmployeeId[empId].includes(signup.id)) {
                    mergedByEmployeeId[empId].push(signup.id);
                }
            });
            
            // Remove any signups that are no longer in the incoming data
            // (they may have been deleted or are from a different fetch)
            Object.keys(mergedShiftSignupsById).forEach((id) => {
                const signup = mergedShiftSignupsById[id];
                const shouldRemove = !incomingIds.has(id);
                if (shouldRemove) {
                    delete mergedShiftSignupsById[id];
                    const empId = signup?.employeeId;
                    if (empId && mergedByEmployeeId[empId]) {
                        mergedByEmployeeId[empId] = mergedByEmployeeId[empId].filter(sid => sid !== id);
                    }
                }
            });
            
            let mergedShiftSignups = {
                byId: mergedShiftSignupsById,
                byEmployeeId: mergedByEmployeeId
            };
            
            // Sort signups by startTime for each employee
            mergedShiftSignups = sortSignupsByStartTime(mergedShiftSignups);
            
            const shiftsReqs = action.requestParams?.shiftsReqs || [];
            const currentShiftName = action.data?.callSheetWorkingShift?.current || '';
            const usedIndices = new Set();
            const currentShiftDetails = shiftsReqs
                .map((r, i) => ({ ...r, _idx: i }))
                .filter((r) => (r.shiftType || '').toUpperCase() === currentShiftName.toUpperCase())
                .map((r) => { usedIndices.add(r._idx); return { startTime: r.startTime || '', endTime: r.endTime || '', unpaidLunch: r.unpaidLunch ?? 'Y', slots: r.slots || 0, workLocation: r.workLocation || '', skills: r.skills || [] }; });
            const matchingReq = currentShiftDetails[0] || {};
            const upcomingRaw = action.data?.callSheetWorkingShift?.upcoming || [];
            const enrichedUpcoming = upcomingRaw.map((entry) => {
                const shiftName = typeof entry === 'string'
                    ? entry
                    : (entry?.shiftName || entry?.shiftType || entry?.name || '');
                const apiStartTime = entry !== null && typeof entry === 'object' ? (entry.startTime || '') : '';
                const apiEndTime = entry !== null && typeof entry === 'object' ? (entry.endTime || '') : '';
                const idx = shiftsReqs.findIndex(
                    (r, i) => !usedIndices.has(i) && (r.shiftType || '').toUpperCase() === shiftName.toUpperCase()
                );
                if (idx >= 0) usedIndices.add(idx);
                const req = idx >= 0 ? shiftsReqs[idx] : {};
                return { name: shiftName, startTime: apiStartTime || req.startTime || '', endTime: apiEndTime || req.endTime || '', unpaidLunch: req.unpaidLunch ?? 'Y' };
            });
            const callSheetData = {
                callSheetId: action.data?.votId || '',
                sessionToken: action.data?.session?.sessionToken || '',
                session: action.data?.session || null,
                callSheetWorkingShift: {
                    ...action.data?.callSheetWorkingShift,
                    upcoming: enrichedUpcoming,
                    slots: action.data?.slots || 0,
                    slotsFilled: action.data?.slotsFilled ?? action.data?.callSheetWorkingShift?.slotsFilled ?? 0,
                    skills: action.data?.skills || '',
                    startTime: matchingReq.startTime || '',
                    endTime: matchingReq.endTime || '',
                    unpaidLunch: matchingReq.unpaidLunch ?? action.data?.callSheetWorkingShift?.unpaidLunch ?? 'Y',
                    currentShiftDetails,
                } || null,
                requestParams: action.requestParams || null
            };
            const customerCareNoContactDisabled = isCustomerCareDepartment(callSheetData?.requestParams?.department);
            // Use API-provided wbPendingCount/pendingCount, or derive from signup data
            // Merge persisted confirmed decisions passed from the saga (pure — no side effects)
            const mergedSignups = mergeWithPersistedDecisions(mergedShiftSignups, action.persistedDecisions);
            const holdRehydratedSignups = customerCareNoContactDisabled
                ? mergedSignups
                : applyNoContactHoldsFromSignups(
                    mergedSignups,
                    currentShiftName,
                    Date.now()
                );
            const pendingCount = action.data?.wbPendingCount ?? action.data?.pendingCount ?? derivePendingCount(holdRehydratedSignups);

            return {
                ...state,
                employees,
                shiftSignups: holdRehydratedSignups,
                noContactTimersByEmployee: customerCareNoContactDisabled
                    ? {}
                    : deriveNoContactTimersByEmployee(
                        holdRehydratedSignups,
                        callSheetData?.callSheetWorkingShift?.current || ''
                    ),
                fetchSuccess: true,
                fetchError: null,
                callSheetData,
                requirementFulfilled: false,
                pendingCount,
            };
        }

        case overtimeConstants.GET_SESSION_STATUS: {
            return {
                ...state,
                sessionStatusSuccess: null,
                sessionStatusError: null,
            };
        }

        case overtimeConstants.GET_SESSION_STATUS_SUCCESS: {
            const incomingSession = action.data || {};

            return {
                ...state,
                sessionStatusData: incomingSession,
                sessionStatusSuccess: true,
                sessionStatusError: null,
            };
        }

        case overtimeConstants.GET_SESSION_STATUS_FAILED: {
            return {
                ...state,
                sessionStatusSuccess: false,
                sessionStatusError: action.data,
            };
        }

        case overtimeConstants.GET_OVERTIME_DATA_FAILED: {
            return {
                ...state,
                fetchSuccess: false,
                fetchError: action.data
            };
        }

        case overtimeConstants.POLL_WB_STATUS:
            return { ...state, wbPolling: true };

        case overtimeConstants.POLL_WB_STATUS_SUCCESS: {
            const { results, allResolved } = action;
            if (!results || Object.keys(results).length === 0) return { ...state, wbPolling: !allResolved };

            const updatedById = { ...state.shiftSignups.byId };
            let changed = false;

            Object.keys(updatedById).forEach((signupId) => {
                const signup = updatedById[signupId];
                const id = String(signup.reqId || signup.transactionId || '');
                if (!id || !results[id]) return;

                const result = results[id];
                // Determine if this was a refuse decision (check selectedDecision or status)
                const isRefusePath = ['REFUSE', 'REFUSED'].includes(
                    (signup.selectedDecision || signup.status || '').replace(/[\s_]/g, '').toUpperCase()
                );
                if (result.status === 'APPLIED' || result.status === 'WB_SUCCESS') {
                    updatedById[signupId] = isRefusePath
                        ? { ...signup, wbStatus: 'WB_SUCCESS', status: 'REFUSED' }
                        : { ...signup, wbStatus: true, status: 'ASSIGNED' };
                    changed = true;
                } else if (result.status === 'WB_FAILED' || result.status === 'WB_FAILURE') {
                    updatedById[signupId] = { ...signup, wbStatus: false, status: 'WB_FAILED' };
                    changed = true;
                }
            });

            if (!changed) return { ...state, wbPolling: false };

            // Decrement slotsTaken on matching signups for WB_FAILED transitions
            // (slot was incremented when ACCEPT was confirmed but WB rejected it)
            Object.keys(updatedById).forEach((signupId) => {
                const updated = updatedById[signupId];
                const original = state.shiftSignups.byId[signupId];
                if (!original || updated.status !== 'WB_FAILED' || original.status === 'WB_FAILED') return;
                const prevFilled = isSlotFilledStatus(original.status);
                const nowFilled = isSlotFilledStatus(updated.status); // false after removal of WBFAILED
                if (prevFilled === nowFilled) return;
                const delta = nowFilled ? 1 : -1;
                const matchKey = [original.shiftName, original.startTime, original.endTime, original.workLocation]
                    .map(v => (v || '').toUpperCase().trim()).join('|');
                Object.keys(updatedById).forEach((id) => {
                    const s = updatedById[id];
                    const sKey = [s.shiftName, s.startTime, s.endTime, s.workLocation]
                        .map(v => (v || '').toUpperCase().trim()).join('|');
                    if (sKey === matchKey) {
                        const current = Number(s.slotsTaken) || 0;
                        updatedById[id] = { ...s, slotsTaken: String(Math.max(0, current + delta)) };
                    }
                });
            });

            // Update slotsFilled for all WB_FAILED transitions
            let updatedCallSheetData = state.callSheetData;
            Object.keys(updatedById).forEach((signupId) => {
                const updated = updatedById[signupId];
                const original = state.shiftSignups.byId[signupId];
                if (!original || updated.status !== 'WB_FAILED' || original.status === 'WB_FAILED') return;
                updatedCallSheetData = updateSlotsFilledCount(updatedCallSheetData, original.status, updated.status);
            });

            const updatedSignups = { ...state.shiftSignups, byId: updatedById };
            return {
                ...state,
                shiftSignups: updatedSignups,
                callSheetData: updatedCallSheetData,
                pendingCount: derivePendingCount(updatedSignups),
                wbPolling: !allResolved,
            };
        }

        case overtimeConstants.TOGGLE_ITEM_COLLAPSED: {
            const employee = state.employees.byId[action.employeeId];
            if (!employee) return state;
            
            const willExpand = employee.collapsed;
            
            // If expanding, collapse all others (accordion behavior)
            const updatedById = {};
            for (const id of state.employees.allIds) {
                const emp = state.employees.byId[id];
                if (id === action.employeeId) {
                    updatedById[id] = { ...emp, collapsed: !emp.collapsed };
                } else if (willExpand) {
                    // Collapse others when expanding this one
                    updatedById[id] = { ...emp, collapsed: true };
                } else {
                    updatedById[id] = emp;
                }
            }
            
            return {
                ...state,
                employees: {
                    ...state.employees,
                    byId: updatedById
                }
            };
        }

        case overtimeConstants.SET_CONTACT: {
            const employee = state.employees.byId[action.employeeId];
            if (!employee) return state;
            
            return {
                ...state,
                employees: {
                    ...state.employees,
                    byId: {
                        ...state.employees.byId,
                        [action.employeeId]: {
                            ...employee,
                            contact: {
                                ...employee.contact,
                                ...action.contact
                            }
                        }
                    }
                }
            };
        }

        case overtimeConstants.IS_CALLED_SUCCESS: {
            const payload = action.data || {};
            const callType = String(payload.callType || '').toUpperCase();
            if (callType !== 'OUTBOUND' && callType !== 'INBOUND') {
                return state;
            }

            const employeeId = payload.empId || payload.employeeId;
            const shiftType = String(payload.shiftType || payload.shiftName || '').trim();
            const votRequestId = String(payload.votRequestId || payload.votId || '').trim();

            if (!employeeId || !shiftType) {
                return state;
            }

            const signupIds = state.shiftSignups.byEmployeeId[employeeId] || [];
            const normalizedShiftType = shiftType.toUpperCase();

            let firstShiftMatchId = null;
            let exactRequestMatchId = null;

            signupIds.forEach((id) => {
                const signup = state.shiftSignups.byId[id];
                if (!signup) return;

                const shiftMatches = (signup.shiftName || '').toUpperCase() === normalizedShiftType;
                if (!shiftMatches) return;

                if (!firstShiftMatchId) {
                    firstShiftMatchId = id;
                }

                if (votRequestId) {
                    const signupRequestId = String(signup.votRequestId || signup.reqId || signup.transactionId || '').trim();
                    if (signupRequestId === votRequestId && !exactRequestMatchId) {
                        exactRequestMatchId = id;
                    }
                }
            });

            // Prefer request-id match, otherwise use first shift match.
            const matchedSignupId = exactRequestMatchId || firstShiftMatchId;

            if (!matchedSignupId) {
                return state;
            }

            const signup = state.shiftSignups.byId[matchedSignupId];
            if (!signup || signup.isCalled === true) {
                return state;
            }

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [matchedSignupId]: {
                            ...signup,
                            isCalled: true,
                        },
                    },
                },
            };
        }

        case overtimeConstants.SELECT_DECISION: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            pendingDecision: action.decision
                        }
                    }
                }
            };
        }

        case overtimeConstants.CONFIRM_DECISION:
        case overtimeConstants.RESOLVE_OT_WB_FAILED: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];

            let updatedShiftSignups = state.shiftSignups;
            if (signup) {
                updatedShiftSignups = {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            isConfirming: true
                        }
                    }
                };
            }

            // Also set isConfirming on matching otSignup entry
            let updatedOtSignups = state.otSignups;
            const otGroups = state.otSignups.byEmployeeId[action.employeeId];
            if (otGroups) {
                const setConfirming = (shiftsArr) =>
                    shiftsArr.map(shift =>
                        shift.id === action.signupId ? { ...shift, isConfirming: true } : shift
                    );
                updatedOtSignups = {
                    byEmployeeId: {
                        ...state.otSignups.byEmployeeId,
                        [action.employeeId]: {
                            otDayShifts: setConfirming(otGroups.otDayShifts || []),
                            previousDayShifts: setConfirming(otGroups.previousDayShifts || []),
                            nextDayShifts: setConfirming(otGroups.nextDayShifts || []),
                        },
                    },
                };
            }

            if (!signup && updatedOtSignups === state.otSignups) return state;

            return {
                ...state,
                shiftSignups: updatedShiftSignups,
                otSignups: updatedOtSignups,
            };
        }

        case overtimeConstants.CONFIRM_DECISION_SUCCESS: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];

            // Also look for a matching otSignup (cards backed by OT signups API
            // have IDs like 'ot-signup-…' that don't exist in shiftSignups.byId)
            const employeeOtGroups = state.otSignups.byEmployeeId[action.employeeId];
            const findOtMatch = (groups) => {
                if (!groups) return null;
                for (const dayKey of ['otDayShifts', 'previousDayShifts', 'nextDayShifts']) {
                    const arr = groups[dayKey] || [];
                    const match = arr.find(s =>
                        s.id === action.signupId ||
                        (action.signupId && s.transactionId && String(s.transactionId) === String(action.signupId.replace(/^ot-signup-[^-]+-/, ''))) ||
                        (s.shiftName === action.shiftName && s.transactionId && action.transactionId && String(s.transactionId) === String(action.transactionId))
                    );
                    if (match) return match;
                }
                return null;
            };
            const otMatch = findOtMatch(employeeOtGroups);

            // Use whichever signup we found as the source for fallback values
            const sourceSignup = signup || otMatch;
            if (!sourceSignup) return state;

            const decision = action.decision || sourceSignup.status || sourceSignup.pendingDecision || '';
            const normalizedDecision = String(decision || '').replace(/[_\s]/g, '').toUpperCase();
            const isRefuseDecision = normalizedDecision === 'REFUSE' || normalizedDecision === 'REFUSED';

            // WB_SUCCESS means Workbrain accepted the shift — treat as ASSIGNED
            const isWbSuccess = normalizedDecision === 'WBSUCCESS';
            // API returned HTTP 200 with result:null + error message — treat as WB_FAILED
            const isWbFailed = normalizedDecision === 'WBFAILED';
            // Reject path should remain pending until WB poll confirms final REFUSED.
            const nextStatus = isWbSuccess ? 'ASSIGNED' : (isRefuseDecision ? 'REFUSE' : decision);
            const nextSelectedDecision = isWbFailed
                ? (sourceSignup.selectedDecision || sourceSignup.pendingDecision || 'APPLY')
                : (isRefuseDecision ? 'REFUSE' : (action.decision || sourceSignup.selectedDecision));
            const nextWbStatus = isWbFailed
                ? false
                : (isRefuseDecision && !isWbSuccess
                    ? 'WB_PENDING'
                    : (isWbSuccess ? true : sourceSignup.wbStatus));
            
            const reqId = action.reqId || sourceSignup.reqId || '';
            const transactionId = action.transactionId || sourceSignup.transactionId || '';
            const lastTimeStamp = action.lastTimeStamp || action.savedAt || new Date().toISOString();

            // Update shiftSignups.byId if the signup exists there
            let updatedById = state.shiftSignups.byId;
            if (signup) {
                updatedById = {
                    ...state.shiftSignups.byId,
                    [signupId]: {
                        ...signup,
                        status: nextStatus,
                        wbStatus: nextWbStatus,
                        selectedDecision: nextSelectedDecision,
                        lastTimeStamp,
                        reqId,
                        transactionId,
                        savedAt: action.savedAt || '',
                        isConfirming: false,
                        ...(action.error ? { error: action.error } : {}),
                    }
                };
            }

            // Update slotsTaken on matching signups when slot-filled status changes
            const prevFilled = isSlotFilledStatus(sourceSignup.status);
            const nowFilled = isSlotFilledStatus(nextStatus);
            let slotsTakenDelta = 0;
            if (prevFilled !== nowFilled) {
                slotsTakenDelta = nowFilled ? 1 : -1;
            }
            if (slotsTakenDelta !== 0) {
                const matchKey = [sourceSignup.shiftName || sourceSignup.shiftType, sourceSignup.startTime, sourceSignup.endTime, sourceSignup.workLocation]
                    .map(v => (v || '').toUpperCase().trim()).join('|');
                Object.keys(updatedById).forEach(id => {
                    const s = updatedById[id];
                    const sKey = [s.shiftName, s.startTime, s.endTime, s.workLocation]
                        .map(v => (v || '').toUpperCase().trim()).join('|');
                    if (sKey === matchKey) {
                        const current = Number(s.slotsTaken) || 0;
                        updatedById[id] = { ...s, slotsTaken: String(Math.max(0, current + slotsTakenDelta)) };
                    }
                });
            }

            const updatedShiftSignups = {
                    ...state.shiftSignups,
                    byId: updatedById,
                };

            // Update matching otSignups entries with new status/selectedDecision/wbStatus
            let updatedOtSignups = state.otSignups;
            if (employeeOtGroups || slotsTakenDelta !== 0) {
                const otUpdateFields = {
                    status: nextStatus,
                    selectedDecision: nextSelectedDecision,
                    wbStatus: nextWbStatus,
                    reqId,
                    transactionId,
                    lastTimeStamp,
                    isConfirming: false,
                    ...(action.error ? { error: action.error } : {}),
                };
                const matchKey = [sourceSignup.shiftName || sourceSignup.shiftType, sourceSignup.startTime, sourceSignup.endTime, sourceSignup.workLocation]
                    .map(v => (v || '').toUpperCase().trim()).join('|');

                const updatedByEmployeeId = { ...state.otSignups.byEmployeeId };

                Object.keys(updatedByEmployeeId).forEach(empId => {
                    const groups = updatedByEmployeeId[empId];
                    if (!groups) return;
                    const isActionEmployee = empId === action.employeeId;
                    let changed = false;

                    const updateArr = (shiftsArr) =>
                        shiftsArr.map(shift => {
                            // Status/decision update only applies to the action's employee
                            if (isActionEmployee) {
                                const isMatch = shift.id === action.signupId ||
                                    (transactionId && String(shift.transactionId) === String(transactionId)) ||
                                    (signup && signup.transactionId && String(shift.transactionId) === String(signup.transactionId));
                                if (isMatch) {
                                    changed = true;
                                    const updated = { ...shift, ...otUpdateFields };
                                    if (slotsTakenDelta !== 0) {
                                        const current = Number(shift.slotsTaken) || 0;
                                        updated.slotsTaken = String(Math.max(0, current + slotsTakenDelta));
                                    }
                                    return updated;
                                }
                            }
                            // Update slotsTaken on all OT signups matching the same shift
                            if (slotsTakenDelta !== 0) {
                                const sKey = [shift.shiftName || shift.shiftType, shift.startTime, shift.endTime, shift.workLocation]
                                    .map(v => (v || '').toUpperCase().trim()).join('|');
                                if (sKey === matchKey) {
                                    changed = true;
                                    const current = Number(shift.slotsTaken) || 0;
                                    return { ...shift, slotsTaken: String(Math.max(0, current + slotsTakenDelta)) };
                                }
                            }
                            return shift;
                        });

                    const updatedGroups = {
                        otDayShifts: updateArr(groups.otDayShifts || []),
                        previousDayShifts: updateArr(groups.previousDayShifts || []),
                        nextDayShifts: updateArr(groups.nextDayShifts || []),
                    };

                    if (changed) {
                        updatedByEmployeeId[empId] = updatedGroups;
                    }
                });

                updatedOtSignups = { byEmployeeId: updatedByEmployeeId };
            }

            return {
                ...state,
                shiftSignups: updatedShiftSignups,
                otSignups: updatedOtSignups,
                callSheetData: updateSlotsFilledCount(state.callSheetData, sourceSignup.status, nextStatus),
                pendingCount: derivePendingCount(updatedShiftSignups),
            };
        }

        case overtimeConstants.SET_OVERTIME_SLOTS: {
            return {
                ...state,
                overtimeSlots: action.data
            };
        }

        case overtimeConstants.RESOLVE_OT_WB_FAILED_SUCCESS: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;

            const isApplied = action.resolveAction === 'MANUALLY-APPLIED';
            const nextStatus = isApplied ? 'ACCEPT' : 'REFUSED';

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            status: nextStatus,
                            // wbStatus: true marks the card as WB-confirmed so isAcceptedVisualState
                            // activates for MANUALLY-APPLIED, showing ACCEPTED pill and locking the card.
                            wbStatus: isApplied ? true : false,
                            selectedDecision: isApplied ? 'APPLY' : 'REFUSED',
                            wbFailureResolveAction: action.resolveAction,
                            isConfirmed: isApplied ? true : false,
                            error: null,
                            resolvedAt: action.resolvedAt || '',
                            lastTimeStamp: action.resolvedAt || new Date().toISOString(),
                            isConfirming: false
                        }
                    }
                },
                callSheetData: updateSlotsFilledCount(state.callSheetData, signup.status, nextStatus),
            };
        }

        case overtimeConstants.CONFIRM_DECISION_FAILED:
        case overtimeConstants.ADD_OT_SHIFT_FAILED:
        case overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];

            // Also clear isConfirming on matching otSignup
            let updatedOtSignups = state.otSignups;
            const failOtGroups = state.otSignups.byEmployeeId[action.employeeId];
            if (failOtGroups) {
                const clearConfirming = (shiftsArr) =>
                    shiftsArr.map(shift =>
                        shift.id === action.signupId ? { ...shift, isConfirming: false, error: action.data } : shift
                    );
                updatedOtSignups = {
                    byEmployeeId: {
                        ...state.otSignups.byEmployeeId,
                        [action.employeeId]: {
                            otDayShifts: clearConfirming(failOtGroups.otDayShifts || []),
                            previousDayShifts: clearConfirming(failOtGroups.previousDayShifts || []),
                            nextDayShifts: clearConfirming(failOtGroups.nextDayShifts || []),
                        },
                    },
                };
            }
            
            return {
                ...state,
                shiftSignups: signup ? {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            error: action.data,
                            confirmed: false,
                            isConfirming: false
                        }
                    }
                } : state.shiftSignups,
                otSignups: updatedOtSignups,
            };
        }

        case overtimeConstants.CLEAR_SIGNUP_ERROR: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];
            if (!signup) return state;
            
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: {
                        ...state.shiftSignups.byId,
                        [signupId]: {
                            ...signup,
                            error: null
                        }
                    }
                }
            };
        }

        case overtimeConstants.GET_CALL_LOG_SUCCESS: {
            const apiEntries = Array.isArray(action.data?.callLog) ? action.data.callLog : [];

            // Parse API timestamp "HH:MM MM/DD/YYYY" into epoch ms (hoisted for hold synthesis reuse)
            const parseApiTimestampMs = (ts) => {
                if (!ts) return Date.now();
                const match = ts.match(/^(\d{1,2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})$/);
                if (match) {
                    const [, h, m, mo, d, y] = match;
                    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(m)).getTime();
                }
                return new Date(ts).getTime() || Date.now();
            };

            const normalized = apiEntries.map((entry, idx) => {
                const timestamp = new Date(parseApiTimestampMs(entry.lastUpdated)).toISOString();
                const shiftLabel = (entry.shiftType || '').replace(/^SHIFT\s*/i, '').trim();

                // Action entries → system-style entries
                if (entry.action) {
                    const actionUpper = entry.action.toUpperCase();
                    const wbStatusUpper = String(entry.wbStatus || '').replace(/\s+/g, '_').toUpperCase();
                    let systemMessage = '';
                    if (actionUpper === 'ACCEPTED' || actionUpper === 'APPLY') {
                        systemMessage = wbStatusUpper === 'WB_SUCCESS'
                            ? 'Accepted Assigned in Workbrain'
                            : 'WB Pending Sent to Workbrain';
                    }
                    else if (actionUpper === 'REJECTED' || actionUpper === 'REFUSE' || actionUpper === 'REFUSED') {
                        systemMessage = wbStatusUpper === 'WB_SUCCESS'
                            ? 'Refused shift'
                            : 'WB Pending Sent to Workbrain';
                    }
                    else if (actionUpper === 'WB_FAILED') systemMessage = `Workbrain upload failed for ${shiftLabel}`;
                    else if (actionUpper === 'SAVE-ACCEPTED') systemMessage = `Saved accepted for ${shiftLabel}`;
                    else if (actionUpper === 'SAVE-REFUSED') systemMessage = `Saved refused for ${shiftLabel}`;
                    else if (actionUpper === 'NOCONTACT') systemMessage = `No Contact for Shift ${shiftLabel}`;
                    else systemMessage = `${entry.action} for ${shiftLabel}`;

                    return {
                        id: `api-${action.employeeId}-${idx}`,
                        isSystem: true,
                        action: entry.action,
                        wbStatus: entry.wbStatus,
                        notes: entry.notes || entry.callNotes || entry.reason || entry.releaseReason || entry.noContactReason || '',
                        callNotes: entry.callNotes || entry.notes || entry.reason || entry.releaseReason || entry.noContactReason || '',
                        transactionId: entry.transactionId || null,
                        systemMessage,
                        shiftLabel,
                        shiftType: entry.shiftType || entry.shiftName || '',
                        shiftName: entry.shiftName || entry.shiftType || '',
                        startTime: entry.startTime || '',
                        endTime: entry.endTime || '',
                        workLocation: entry.workLocation || '',
                        timestamp,
                        isFromApi: true,
                        holdStatus: entry.holdStatus || '',
                    };
                }

                // Call entries → call-style entries
                const callTypeVal = (entry.callType || '').charAt(0).toUpperCase() + (entry.callType || '').slice(1).toLowerCase();
                return {
                    id: `api-${action.employeeId}-${idx}`,
                    type: callTypeVal,
                    answered: entry.isAnswered ? 'answered' : (entry.isCallReturned ? 'received voicemail' : 'no answer'),
                    returnedCall: entry.isCallReturned ? 'yes' : 'no',
                    outcome: '',
                    notes: entry.callNotes || '',
                    shiftLabel,
                    shiftType: entry.shiftType || '',
                    timestamp,
                    isFromApi: true,
                    action: entry.action,
                    holdStatus: entry.holdStatus || '',

                };
            });

            // Synthesize no-contact hold for Customer Operations no-answer entries
            const HOLD_DURATION_MS = 10 * 60 * 1000;
            const department = state.callSheetData?.requestParams?.department || '';
            let newNoContactHolds = state.noContactHolds;
            if (/customer.operations/i.test(department)) {
                // If API says hold is expired, evict any existing hold for this employee
                const hasExpiredHold = apiEntries.some(e =>
                    (e?.holdStatus || '').toUpperCase() === 'HOLD_EXPIRED'
                );
                if (hasExpiredHold) {
                    const empIdStr = String(action.employeeId || '');
                    const filtered = {};
                    Object.entries(newNoContactHolds || {}).forEach(([key, hold]) => {
                        if (hold.employeeId !== action.employeeId &&
                            !(hold.transactionId && empIdStr && hold.transactionId.startsWith(empIdStr + '-'))) {
                            filtered[key] = hold;
                        }
                    });
                    newNoContactHolds = filtered;
                }

                // Look for NOCONTACT action entries or no-answer entries, excluding already-expired holds
                const noContactEntries = apiEntries.filter(e => {
                    if ((e?.holdStatus || '').toUpperCase() === 'HOLD_EXPIRED') return false;
                    const isNoContact = isNoContactAction(e?.action);
                    const isNoAnswer = !e.action && !e.isAnswered && !e.isCallReturned;
                    return isNoContact || isNoAnswer;
                });
                
                if (noContactEntries.length > 0) {
                    const latest = noContactEntries.reduce((a, b) =>
                        parseApiTimestampMs(b.lastUpdated) > parseApiTimestampMs(a.lastUpdated) ? b : a
                    );
                    const startedAt = parseApiTimestampMs(latest.lastUpdated);
                    
                    // Check if a subsequent answer, voicemail, or explicit release cancels the hold
                    const hasSubsequentAnswer = apiEntries.some(e => {
                        const ts = parseApiTimestampMs(e.lastUpdated);
                        if (ts <= startedAt) return false;
                        return e.isAnswered ||
                            e.isCallReturned ||
                            normalizeActionToken(e?.action) === 'NOCONTACTRELEASED';
                    });

                    if (!hasSubsequentAnswer) {
                        const shiftLabel = normalizeShiftToken(latest.shiftType || '');
                        const employeeSignupIds = state.shiftSignups.byEmployeeId[action.employeeId] || [];
                        const matchingSignup = employeeSignupIds
                            .map(id => state.shiftSignups.byId[id])
                            .find(s => s && normalizeShiftToken(s.shiftName || s.shiftType || '') === shiftLabel);

                        // Skip re-adding if the hold was intentionally released this session.
                        // noContactResolved is set by RELEASE_NO_CONTACT_HOLD; without this guard,
                        // the saga's immediate GET_CALL_LOG re-fetch (before the API processes
                        // NOCONTACT_RELEASED) would re-add the hold and create an infinite release loop.
                        // Also require noContactHoldApplied === true so that historical call-log entries
                        // from a previous session (where noContactResolved was lost after OVERTIME_CLEANUP)
                        // do not re-synthesize a hold in a brand-new session.
                        if (matchingSignup && matchingSignup.noContactResolved !== true && matchingSignup.noContactHoldApplied === true) {
                            const holdKey = matchingSignup.transactionId ||
                                `${String(action.employeeId || '')}-${String(latest.shiftType || '')}-nch`;
                            const employee = state.employees.byId[action.employeeId];
                            // Preserve existing startedAt so the API's minute-precision timestamp
                            // never overwrites the exact Date.now() captured at hold creation.
                            const existingHold = state.noContactHolds?.[holdKey];
                            const preservedStartedAt = existingHold?.startedAt || startedAt;
                            // Don't re-synthesize an already-expired hold; also evict it from state.
                            if (preservedStartedAt + HOLD_DURATION_MS <= Date.now()) {
                                const { [holdKey]: _evicted, ...remainingHolds } = state.noContactHolds;
                                newNoContactHolds = remainingHolds;
                            } else {
                                newNoContactHolds = {
                                    ...state.noContactHolds,
                                    [holdKey]: {
                                        transactionId: holdKey,
                                        employeeId: action.employeeId,
                                        firstName: employee?.firstName || '',
                                        lastName: employee?.lastName || '',
                                        shiftName: matchingSignup.shiftName || matchingSignup.shiftType || '',
                                        startTime: matchingSignup.startTime || '',
                                        endTime: matchingSignup.endTime || '',
                                        workLocation: matchingSignup.workLocation || '',
                                        startedAt: preservedStartedAt,
                                        durationMs: HOLD_DURATION_MS,
                                    },
                                };
                            }
                        }
                    }
                }
            }

            return {
                ...state,
                callLogEntries: {
                    ...state.callLogEntries,
                    [action.employeeId]: normalized,
                },
                noContactHolds: newNoContactHolds,
            };
        }

        case overtimeConstants.SYNC_NO_CONTACT_TIMER_NOW:
            return {
                ...state,
                timerNowMs: Number(action.nowMs) || Date.now(),
            };

        case overtimeConstants.SET_CONTINUE_IN_PROGRESS:
            return {
                ...state,
                continueInProgress: Boolean(action.value),
            };

        case overtimeConstants.POST_CALL_LOG: {
            const callData = action.data || {};
            const employeeId = callData.empId || callData.employeeId || action.employeeId;
            if (!employeeId) {
                return state;
            }

            // Normalize callType to camelCase (same as API does)
            const callTypeVal = (callData.callType || '').charAt(0).toUpperCase() + (callData.callType || '').slice(1).toLowerCase();

            // Create new entry from posted call data
            const newEntry = {
                id: `local-${Date.now()}-${Math.random()}`,
                type: callTypeVal || 'Inbound',
                answered: callData.isAnswered ? 'answered' : (callData.isCallReturned ? 'received voicemail' : 'no answer'),
                returnedCall: callData.isCallReturned ? 'yes' : 'no',
                outcome: callData.outcome || '',
                notes: callData.callNotes || '',
                shiftLabel: callData.shiftType || '',
                shiftType: callData.shiftType || '',
                shiftName: callData.shiftType || '',
                startTime: callData.startTime || '',
                endTime: callData.endTime || '',
                workLocation: callData.workLocation || '',
                timestamp: callData.lastUpdated || new Date().toISOString(),
                isFromApi: false, // Local entry, not from API yet
                employeeId: employeeId,
                callType: callTypeVal,
            };

            // Add new entry to beginning of call log entries for this employee
            const existingEntries = state.callLogEntries?.[employeeId] || [];
            const updatedEntries = [newEntry, ...existingEntries];
            
            return {
                ...state,
                callLogEntries: {
                    ...state.callLogEntries,
                    [employeeId]: updatedEntries,
                },
            };
        }

        case overtimeConstants.GET_OT_SIGNUPS_SUCCESS: {
            const otShiftSignUps = action.data?.otShiftSignUps || {};
            const currentSessionToken = String(state.callSheetData?.sessionToken || '').trim();
            const normalizeSessionToken = (value) => String(value || '').trim();
            const normalizeShift = (shift, idx, dayType) => ({
                id: `ot-signup-${action.employeeId}-${[
                    shift.transactionId,
                    shift.reqId,
                    shift.sessionToken,
                    shift.shiftName,
                    shift.startTime,
                    shift.endTime,
                    shift.workLocation,
                    idx,
                ].filter((part) => String(part || '').trim() !== '').join('-')}`,
                shiftName: shift.shiftName || '',
                shiftType: shift.shiftName || '',
                restrictions: shift.restrictions || '',
                preferences: shift.preferences || '',
                autoSignUp: shift.autoSignUp,
                multiShifts: shift.multiShifts,
                startTime: shift.startTime,
                endTime: shift.endTime,
                workLocation: shift.workLocation,
                slotsOffered: shift.slotsOffered,
                slotsTaken: shift.slotsTaken || '0',
                isEligible: shift.isEligible,
                notEligibleNotes: shift.notEligibleNotes || '',
                selectedDecision: shift.selectedDecision,
                isConfirmed: shift.isConfirmed,
                lastTimeStamp: shift.lastTimeStamp,
                transactionId: shift.transactionId,
                reqId: shift.reqId || '',
                wbStatus: shift.wbStatus || '',
                status: shift.wbStatus || '',
                shiftDate: shift.shiftDate || '',
                dayType,
                isOtSignup: true,
                sessionToken: normalizeSessionToken(shift.sessionToken),
            });

            const normalized = {
                otDayShifts: (otShiftSignUps.otDayShifts || []).map((shift, idx) => normalizeShift(shift, idx, 'current')),
                previousDayShifts: (otShiftSignUps.previousDayShifts || []).map((shift, idx) => normalizeShift(shift, idx, 'previous')),
                nextDayShifts: (otShiftSignUps.nextDayShifts || []).map((shift, idx) => normalizeShift(shift, idx, 'next')),
            };

            // Merge OT signup updates into existing shiftSignups.byId when there's a
            // match by transactionId or by field identity (shiftName + startTime + endTime + workLocation).
            // This prevents duplicate cards when the /signup API returns an update
            // for a card the callsheet already holds.
            const updatedShiftSignupsById = { ...state.shiftSignups.byId };
            const employeeSignupIds = state.shiftSignups.byEmployeeId[action.employeeId] || [];
            let shiftSignupsChanged = false;
            const mergedOtDayIndices = new Set();

            // Normalize time values to HH:MM for comparison across different formats
            const toHHMM = (value) => {
                const token = normalizeSlotTimeToken(value);
                return /^\d{2}:\d{2}$/.test(token) ? token : '';
            };

            (otShiftSignUps.otDayShifts || []).forEach((shift, idx) => {
                const otSessionToken = normalizeSessionToken(shift.sessionToken);
                const hasComparableSessionTokens = Boolean(currentSessionToken && otSessionToken);
                const isOtherSessionSignup = hasComparableSessionTokens && otSessionToken !== currentSessionToken;
                if (isOtherSessionSignup) {
                    return;
                }

                const otTxnId = String(shift.transactionId || '').trim();
                const otShiftName = (shift.shiftName || '').toUpperCase().trim();
                const otStart = toHHMM(shift.startTime);
                const otEnd = toHHMM(shift.endTime);
                const otWorkLoc = (shift.workLocation || '').toUpperCase().trim();
                let matchedId = null;

                // Strategy 1: match by transactionId
                if (otTxnId) {
                    matchedId = employeeSignupIds.find((id) => {
                        const s = updatedShiftSignupsById[id];
                        return s && String(s.transactionId || '').trim() === otTxnId;
                    });
                }

                // Strategy 2: match by field identity (shiftName + startTime + endTime + workLocation)
                if (!matchedId && otShiftName && otStart && otEnd) {
                    matchedId = employeeSignupIds.find((id) => {
                        const s = updatedShiftSignupsById[id];
                        if (!s) return false;
                        return (
                            (s.shiftName || s.shiftType || '').toUpperCase().trim() === otShiftName &&
                            toHHMM(s.startTime) === otStart &&
                            toHHMM(s.endTime) === otEnd &&
                            (s.workLocation || '').toUpperCase().trim() === otWorkLoc
                        );
                    });
                }

                // Strategy 3: match by shiftName + startTime + endTime (without workLocation)
                if (!matchedId && otShiftName && otStart && otEnd) {
                    matchedId = employeeSignupIds.find((id) => {
                        const s = updatedShiftSignupsById[id];
                        if (!s) return false;
                        return (
                            (s.shiftName || s.shiftType || '').toUpperCase().trim() === otShiftName &&
                            toHHMM(s.startTime) === otStart &&
                            toHHMM(s.endTime) === otEnd
                        );
                    });
                }

                // Strategy 4: match by shiftName only when there is exactly one signup for that shift
                if (!matchedId && otShiftName) {
                    const shiftNameCandidates = employeeSignupIds.filter((id) => {
                        const s = updatedShiftSignupsById[id];
                        return s && (s.shiftName || s.shiftType || '').toUpperCase().trim() === otShiftName;
                    });
                    if (shiftNameCandidates.length === 1) {
                        matchedId = shiftNameCandidates[0];
                    }
                }

                if (!matchedId) return;

                // Merge server fields onto existing shiftSignup.
                // When the API returns a confirmed decision (isConfirmed: true),
                // update status/selectedDecision — those are authoritative from WB.
                const existing = updatedShiftSignupsById[matchedId];
                const isConfirmedFromApi = shift.isConfirmed === true;
                const hasSelectedDecisionFromApi = String(shift.selectedDecision || '').trim() !== '';
                const deriveStatus = () => {
                    if (!isConfirmedFromApi) return existing.status;
                    const decision = (shift.selectedDecision || '').toUpperCase();
                    const wbStat = (shift.wbStatus || '').replace(/[\s_]/g, '').toUpperCase();
                    if (decision === 'REFUSE' || decision === 'REFUSED') {
                        return wbStat === 'WBSUCCESS' ? 'REFUSED' : 'REFUSE';
                    }
                    if (decision === 'APPLY' || decision === 'ACCEPT') {
                        return wbStat === 'WBSUCCESS' ? 'ASSIGNED' : 'ACCEPT';
                    }
                    return shift.wbStatus || existing.status;
                };

                updatedShiftSignupsById[matchedId] = {
                    ...existing,
                    transactionId: shift.transactionId || existing.transactionId || '',
                    reqId: shift.reqId || existing.reqId || '',
                    wbStatus: shift.wbStatus || existing.wbStatus || '',
                    lastTimeStamp: shift.lastTimeStamp || existing.lastTimeStamp || '',
                    slotsTaken: shift.slotsTaken || existing.slotsTaken || '0',
                    isEligible: shift.isEligible != null ? shift.isEligible : existing.isEligible,
                    notEligibleNotes: shift.notEligibleNotes || existing.notEligibleNotes || '',
                    sessionToken: otSessionToken || existing.sessionToken || '',
                    ...(hasSelectedDecisionFromApi && {
                        selectedDecision: shift.selectedDecision,
                    }),
                    // Update decision/status fields when API confirms them
                    ...(isConfirmedFromApi && {
                        isConfirmed: true,
                        status: deriveStatus(),
                    }),
                };
                shiftSignupsChanged = true;
                mergedOtDayIndices.add(idx);
            });

            // Filter merged entries out of otDayShifts so they don't render twice
            const filteredOtDayShifts = normalized.otDayShifts.filter((_, idx) => !mergedOtDayIndices.has(idx));

            return {
                ...state,
                shiftSignups: shiftSignupsChanged
                    ? { ...state.shiftSignups, byId: updatedShiftSignupsById }
                    : state.shiftSignups,
                otSignups: {
                    byEmployeeId: {
                        ...state.otSignups.byEmployeeId,
                        [action.employeeId]: {
                            ...normalized,
                            otDayShifts: filteredOtDayShifts,
                        },
                    },
                },
            };
        }

        case overtimeConstants.OVERTIME_CLEANUP: {
            removeNoContactHolds();
            return {
                ...state,
                employees: { byId: {}, allIds: [] },
                shiftSignups: { byId: {}, byEmployeeId: {} },
                overtimeSlots: [],
                fetchSuccess: null,
                fetchError: null,
                callSheetData: null,
                otSkillsSuccess: null,
                otSkillsData: null,
                sessionStatusData: null,
                sessionStatusSuccess: null,
                sessionStatusError: null,
                requirementFulfilled: false,
                pendingCount: null,
                callLogEntries: {},
                noContactTimersByEmployee: {},
                timerNowMs: 0,
                continueInProgress: false,
                otSignups: { byEmployeeId: {} },
                noContactHolds: {},
            };
        }

        case overtimeConstants.GET_OT_SKILLS_DATA: {
            return {
                ...state,
                otSkillsData: action.payload
            };
        }

        case overtimeConstants.GET_OT_SKILLS_SUCCESS: {
            return {
                ...state,
                otSkillsSuccess: action.payload
            };
        }

        case overtimeConstants.SLOT_FULFILLED: {
            return {
                ...state,
                requirementFulfilled: true
            };
        }

        case overtimeConstants.RESET_SLOT_FULFILLED: {
            return {
                ...state,
                requirementFulfilled: false
            };
        }

        case overtimeConstants.GET_VOT_REPORT: {
            return {
                ...state,
                votReportLoading: true,
                votReportError: null,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_DETAIL: {
            return {
                ...state,
                votReportDetailLoading: true,
                votReportDetailError: null,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_SUCCESS: {
            return {
                ...state,
                votReportData: Array.isArray(action.data) ? action.data : [],
                votReportLoading: false,
                votReportError: null,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_FAILED: {
            return {
                ...state,
                votReportLoading: false,
                votReportError: action.data,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_DETAIL_SUCCESS: {
            return {
                ...state,
                votReportDetailData: action.data || {},
                votReportDetailLoading: false,
                votReportDetailError: null,
            };
        }

        case overtimeConstants.GET_VOT_REPORT_DETAIL_FAILED: {
            return {
                ...state,
                votReportDetailLoading: false,
                votReportDetailError: action.data,
            };
        }

        case overtimeConstants.GET_VOT_CALLSHEET: {
            return {
                ...state,
                votCallsheetData: [],
                votCallsheetLoading: true,
                votCallsheetError: null,
            };
        }

        case overtimeConstants.GET_VOT_CALLSHEET_SUCCESS: {
            return {
                ...state,
                votCallsheetData: Array.isArray(action.data) ? action.data : [],
                votCallsheetLoading: false,
                votCallsheetError: null,
            };
        }

        case overtimeConstants.GET_VOT_CALLSHEET_FAILED: {
            return {
                ...state,
                votCallsheetLoading: false,
                votCallsheetError: action.data,
            };
        }

        case overtimeConstants.CHECK_DUPLICATE_SHIFT_SUCCESS: {
            if (action.payload?.duplicate === true) {
                const existingVotMessage = action.payload?.existingVotMessage || 'Shift already exists.';
                const filledSlots = action.payload?.filledSlots ?? action.payload?.currentFilled ?? action.payload?.slotsFilled ?? 0;
                const totalSlots = action.payload?.totalSlots ?? null;
                return {
                    ...state,
                    duplicateModal: {
                        status: 'resolved',
                        show: true,
                        error: false,
                        message: totalSlots != null
                            ? `${existingVotMessage} with #${filledSlots} of ${totalSlots} slots filled`
                            : `${existingVotMessage} with #${filledSlots} slots filled`,
                        existingShift: {
                            startTime: action.payload?.startTime || null,
                            endTime: action.payload?.endTime || null,
                            workLocation: action.payload?.workLocation || null,
                            totalSlots,
                            openSlots: action.payload?.openSlots ?? null,
                            filledSlots,
                            createdBy: action.payload?.createdBy || null,
                            createdAt: action.payload?.createdAt || null
                        }
                    }
                };
            }
            // If not duplicate, clear modal but mark the API response as resolved.
            return {
                ...state,
                duplicateModal: {
                    status: 'resolved',
                    show: false,
                    error: false,
                    message: ''
                }
            };
        }

        case overtimeConstants.CHECK_DUPLICATE_SHIFT_FAILED: {
            return {
                ...state,
                duplicateModal: {
                    status: 'resolved',
                    show: false,
                    error: true,
                    message: action.data || 'Unable to verify duplicate shift. Please try again.'
                }
            };
        }

        case overtimeConstants.CHECK_DUPLICATE_SHIFT_REQUEST: {
            return {
                ...state,
                duplicateModal: {
                    ...state.duplicateModal,
                    status: 'loading',
                    error: false,
                    show: false,
                    message: ''
                }
            };
        }

        case overtimeConstants.RESET_DUPLICATE_SHIFT_MODAL: {
            return {
                ...state,
                duplicateModal: {
                    status: null,
                    show: false,
                    message: '',
                    error: false,
                    existingShift: null
                }
            };
        }

        case overtimeConstants.SET_CALL_LOG_GATE: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];
            if (signup) {
                return {
                    ...state,
                    shiftSignups: {
                        ...state.shiftSignups,
                        byId: {
                            ...state.shiftSignups.byId,
                            [signupId]: {
                                ...signup,
                                callLogGateOpen: true
                            }
                        }
                    }
                };
            }
            // OT-signup-backed cards: find and update in otSignups.byEmployeeId
            const setGateOtResult = updateOtSignupField(state, action, { callLogGateOpen: true });
            return setGateOtResult || state;
        }

        case overtimeConstants.CLEAR_CALL_LOG_GATE: {
            const signupId = resolveSignupId(state, action);
            const signup = state.shiftSignups.byId[signupId];
            if (signup) {
                return {
                    ...state,
                    shiftSignups: {
                        ...state.shiftSignups,
                        byId: {
                            ...state.shiftSignups.byId,
                            [signupId]: {
                                ...signup,
                                callLogGateOpen: false
                            }
                        }
                    }
                };
            }
            // OT-signup-backed cards: find and update in otSignups.byEmployeeId
            const clearGateOtResult = updateOtSignupField(state, action, { callLogGateOpen: false });
            return clearGateOtResult || state;
        }

        case overtimeConstants.RELEASE_NO_CONTACT_HOLD: {
            const targetShiftToken = normalizeShiftType(action.shiftType);
            const ownerEmployeeId = String(action.employeeId || '').trim();

            // Compute remaining holds (remove the released employee's hold)
            const remainingHolds = Object.fromEntries(
                Object.entries(state.noContactHolds || {}).filter(([, hold]) => {
                    if (!ownerEmployeeId) return true;
                    const holdEmployeeId = String(hold?.employeeId || '').trim();
                    if (holdEmployeeId && holdEmployeeId === ownerEmployeeId) return false;
                    const holdTxnId = String(hold?.transactionId || '').trim();
                    return !(holdTxnId && holdTxnId.startsWith(`${ownerEmployeeId}-`));
                })
            );

            // Find the released hold to get its slot details for accurate slot-group matching
            const releasedHold = Object.values(state.noContactHolds || {}).find((h) => {
                const hEmpId = String(h?.employeeId || '').trim();
                if (ownerEmployeeId && hEmpId === ownerEmployeeId) return true;
                const hTxnId = String(h?.transactionId || '').trim();
                return ownerEmployeeId && hTxnId.startsWith(`${ownerEmployeeId}-`);
            }) || null;

            const releaseShiftName = normalizeShiftType(releasedHold?.shiftName || action.shiftType || '');
            const releaseStartToken = normalizeSlotTimeToken(releasedHold?.startTime || '');
            const releaseEndToken = normalizeSlotTimeToken(releasedHold?.endTime || '');
            const releaseLocation = normalizeShiftType(releasedHold?.workLocation || '');

            const matchesSlotGroup = (h) =>
                h && releaseShiftName &&
                normalizeShiftType(h.shiftName || '') === releaseShiftName &&
                (!releaseStartToken || normalizeSlotTimeToken(h.startTime || '') === releaseStartToken) &&
                (!releaseEndToken || normalizeSlotTimeToken(h.endTime || '') === releaseEndToken) &&
                (!releaseLocation || normalizeShiftType(h.workLocation || '') === releaseLocation);

            const oldHoldsCount = Object.values(state.noContactHolds || {}).filter(matchesSlotGroup).length;
            const remainingHoldsCount = Object.values(remainingHolds).filter(matchesSlotGroup).length;

            const nextById = { ...state.shiftSignups.byId };
            Object.keys(nextById).forEach((id) => {
                const signup = nextById[id];
                if (!signup) return;
                const signupShiftToken = normalizeShiftType(signup.shiftName || signup.shiftType);

                // Match by slot group details, falling back to shift name only
                const matchesBySlot = releaseShiftName &&
                    signupShiftToken === releaseShiftName &&
                    (!releaseStartToken || normalizeSlotTimeToken(signup.startTime || '') === releaseStartToken) &&
                    (!releaseEndToken || normalizeSlotTimeToken(signup.endTime || '') === releaseEndToken) &&
                    (!releaseLocation || normalizeShiftType(signup.workLocation || '') === releaseLocation);
                const matchesByShiftOnly = targetShiftToken && signupShiftToken === targetShiftToken;

                if (!matchesBySlot && !matchesByShiftOnly) return;

                const isOwnerSignup = ownerEmployeeId && String(signup.employeeId || '').trim() === ownerEmployeeId;

                const hasSlotDetails = releaseStartToken || releaseEndToken || releaseLocation;
                const currentSlotsTaken = Number(signup.slotsTaken) || 0;
                let nextSlotsTaken;
                if (hasSlotDetails && matchesBySlot) {
                    // Have slot details: use hold-count math for precision
                    const baseSlotsTaken = Math.max(0, currentSlotsTaken - oldHoldsCount);
                    nextSlotsTaken = baseSlotsTaken + remainingHoldsCount;
                } else if (!hasSlotDetails && matchesByShiftOnly) {
                    // No slot details (e.g., hydrated from sessionStorage): decrement by 1
                    // for all signups in the shift — best-effort with no slot-group data.
                    nextSlotsTaken = Math.max(0, currentSlotsTaken - 1);
                } else {
                    return; // Don't touch signups we can't correctly adjust
                }

                nextById[id] = {
                    ...signup,
                    slotsTaken: String(nextSlotsTaken),
                    ...(isOwnerSignup ? {
                        noContactHoldApplied: false,
                        noContactHoldUntilMs: 0,
                        noContactHoldSourceEmployeeId: '',
                        noContactResolved: true,
                    } : {}),
                };
            });

            const nextTimers = { ...state.noContactTimersByEmployee };
            if (ownerEmployeeId) {
                delete nextTimers[ownerEmployeeId];
            }

            saveNoContactHolds(remainingHolds);
            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: nextById,
                },
                noContactTimersByEmployee: nextTimers,
                noContactHolds: remainingHolds,
            };
        }

        case overtimeConstants.SET_NO_CONTACT_HOLD: {
            const { hold } = action;
            if (!hold || !hold.transactionId || String(hold.transactionId).trim() === '') return state;
            const newHolds = {
                ...state.noContactHolds,
                [hold.transactionId]: hold,
            };
            saveNoContactHolds(newHolds);

            // Immediately reserve the slot on the matching signup so availability updates
            // before the next GET_OVERTIME_DATA_SUCCESS refetch arrives.
            const holdEmployeeId = String(hold.employeeId || '').trim();
            const holdShiftName = normalizeShiftType(hold.shiftName || '');
            const holdStartToken = normalizeSlotTimeToken(hold.startTime || '');
            const holdEndToken = normalizeSlotTimeToken(hold.endTime || '');
            const holdLocation = normalizeShiftType(hold.workLocation || '');
            const holdUntilMs = (Number(hold.startedAt) || Date.now()) + (Number(hold.durationMs) || 600000);

            // Count how many holds (before and after this one) belong to this slot group.
            // This is the source of truth for slotsTaken so multiple concurrent holds
            // each increment the counter correctly.
            const matchesSlotGroup = (h) =>
                h && holdShiftName &&
                normalizeShiftType(h.shiftName || '') === holdShiftName &&
                (!holdStartToken || normalizeSlotTimeToken(h.startTime || '') === holdStartToken) &&
                (!holdEndToken || normalizeSlotTimeToken(h.endTime || '') === holdEndToken) &&
                (!holdLocation || normalizeShiftType(h.workLocation || '') === holdLocation);

            const oldHoldsCount = Object.values(state.noContactHolds || {}).filter(matchesSlotGroup).length;
            const newHoldsCount = Object.values(newHolds).filter(matchesSlotGroup).length;

            const updatedById = { ...state.shiftSignups.byId };
            Object.keys(updatedById).forEach((id) => {
                const signup = updatedById[id];
                if (!signup) return;
                // Match by transactionId first (exact signup), then by slot details
                const matchesById = String(signup.transactionId || '').trim() === hold.transactionId;
                const matchesBySlot = holdShiftName &&
                    normalizeShiftType(signup.shiftName || signup.shiftType || '') === holdShiftName &&
                    (!holdStartToken || normalizeSlotTimeToken(signup.startTime || '') === holdStartToken) &&
                    (!holdEndToken || normalizeSlotTimeToken(signup.endTime || '') === holdEndToken) &&
                    (!holdLocation || normalizeShiftType(signup.workLocation || '') === holdLocation);
                if (!matchesById && !matchesBySlot) return;

                // Compute slotsTaken from hold counts: strip the old hold contribution,
                // then add the new hold count. This correctly handles multiple concurrent holds.
                const currentSlotsTaken = Number(signup.slotsTaken) || 0;
                const baseSlotsTaken = Math.max(0, currentSlotsTaken - oldHoldsCount);
                const newSlotsTaken = baseSlotsTaken + newHoldsCount;

                // Only set ownership flags on this specific employee's signup.
                // Other signups in the same slot get their slotsTaken updated only.
                const isOwnerSignup = String(signup.employeeId || '').trim() === holdEmployeeId;
                updatedById[id] = {
                    ...signup,
                    slotsTaken: String(newSlotsTaken),
                    ...(isOwnerSignup ? {
                        noContactHoldApplied: true,
                        noContactHoldUntilMs: holdUntilMs,
                        noContactHoldSourceEmployeeId: holdEmployeeId,
                        // Clear the resolved flag so GET_CALL_LOG_SUCCESS synthesize can re-hydrate
                        // if the same employee gets a new hold after a previous release.
                        noContactResolved: false,
                    } : {}),
                };
            });

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: updatedById,
                },
                noContactHolds: newHolds,
            };
        }

        case overtimeConstants.CLEAR_NO_CONTACT_HOLD: {
            const { [action.transactionId]: removedHold, ...remaining } = state.noContactHolds;
            saveNoContactHolds(remaining);

            if (!removedHold) {
                return { ...state, noContactHolds: remaining };
            }

            const clearEmployeeId = String(removedHold.employeeId || '').trim();
            const clearShiftName = normalizeShiftType(removedHold.shiftName || '');
            const clearStartToken = normalizeSlotTimeToken(removedHold.startTime || '');
            const clearEndToken = normalizeSlotTimeToken(removedHold.endTime || '');
            const clearLocation = normalizeShiftType(removedHold.workLocation || '');

            const matchesSlotGroup = (h) =>
                h && clearShiftName &&
                normalizeShiftType(h.shiftName || '') === clearShiftName &&
                (!clearStartToken || normalizeSlotTimeToken(h.startTime || '') === clearStartToken) &&
                (!clearEndToken || normalizeSlotTimeToken(h.endTime || '') === clearEndToken) &&
                (!clearLocation || normalizeShiftType(h.workLocation || '') === clearLocation);

            const oldHoldsCount = Object.values(state.noContactHolds || {}).filter(matchesSlotGroup).length;
            const remainingHoldsCount = Object.values(remaining).filter(matchesSlotGroup).length;

            const clearById = { ...state.shiftSignups.byId };
            Object.keys(clearById).forEach((id) => {
                const signup = clearById[id];
                if (!signup) return;

                const matchesBySlot = clearShiftName &&
                    normalizeShiftType(signup.shiftName || signup.shiftType || '') === clearShiftName &&
                    (!clearStartToken || normalizeSlotTimeToken(signup.startTime || '') === clearStartToken) &&
                    (!clearEndToken || normalizeSlotTimeToken(signup.endTime || '') === clearEndToken) &&
                    (!clearLocation || normalizeShiftType(signup.workLocation || '') === clearLocation);
                const matchesById = String(signup.transactionId || '').trim() === action.transactionId;

                if (!matchesBySlot && !matchesById) return;

                const currentSlotsTaken = Number(signup.slotsTaken) || 0;
                const baseSlotsTaken = Math.max(0, currentSlotsTaken - oldHoldsCount);
                const newSlotsTaken = baseSlotsTaken + remainingHoldsCount;

                const isOwnerSignup = String(signup.employeeId || '').trim() === clearEmployeeId;
                clearById[id] = {
                    ...signup,
                    slotsTaken: String(newSlotsTaken),
                    ...(isOwnerSignup ? {
                        noContactHoldApplied: false,
                        noContactHoldUntilMs: 0,
                        noContactHoldSourceEmployeeId: '',
                    } : {}),
                };
            });

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: clearById,
                },
                noContactHolds: remaining,
            };
        }

        case overtimeConstants.CLEAR_ALL_NO_CONTACT_HOLDS: {
            // Undo all slot reservations and wipe noContactHolds entirely.
            removeNoContactHolds();
            const allHolds = Object.values(state.noContactHolds || {});
            const allClearById = { ...state.shiftSignups.byId };

            // Build slot-group → hold count map from the active holds
            // so we can correctly remove the hold contribution from each signup's slotsTaken.
            const holdsBySlotGroup = {};
            allHolds.forEach((h) => {
                const key = [
                    normalizeShiftType(h.shiftName || ''),
                    normalizeSlotTimeToken(h.startTime || ''),
                    normalizeSlotTimeToken(h.endTime || ''),
                    normalizeShiftType(h.workLocation || ''),
                ].join('|');
                holdsBySlotGroup[key] = (holdsBySlotGroup[key] || 0) + 1;
            });

            Object.keys(allClearById).forEach((id) => {
                const signup = allClearById[id];
                if (!signup) return;
                const slotKey = [
                    normalizeShiftType(signup.shiftName || signup.shiftType || ''),
                    normalizeSlotTimeToken(signup.startTime || ''),
                    normalizeSlotTimeToken(signup.endTime || ''),
                    normalizeShiftType(signup.workLocation || ''),
                ].join('|');
                const holdsForSlot = holdsBySlotGroup[slotKey] || 0;
                if (holdsForSlot === 0 && signup.noContactHoldApplied !== true) return;
                const current = Number(signup.slotsTaken) || 0;
                allClearById[id] = {
                    ...signup,
                    slotsTaken: String(Math.max(0, current - holdsForSlot)),
                    noContactHoldApplied: false,
                    noContactHoldUntilMs: 0,
                    noContactHoldSourceEmployeeId: '',
                };
            });

            return {
                ...state,
                shiftSignups: {
                    ...state.shiftSignups,
                    byId: allClearById,
                },
                noContactHolds: {},
            };
        }

        default:
            return state;
    }
};

export default OvertimeReducer;
